import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { FIELD_CHROME } from '@/lib/field-chrome';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'aspect-square h-5 w-5 shrink-0 rounded-full border border-zinc-400 bg-white text-primary shadow-sm transition-colors hover:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:border-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:bg-black dark:hover:border-primary',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex h-full w-full items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-primary text-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

type RadioGroupOptionProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
  label: React.ReactNode;
};

const RadioGroupOption = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupOptionProps
>(({ className, label, id, ...props }, ref) => {
  const reactId = React.useId();
  const optionId = id ?? reactId;
  return (
    <label
      htmlFor={optionId}
      className={cn(
        FIELD_CHROME.solid.base,
        'flex cursor-pointer items-center gap-3 rounded-xl px-4 py-3',
        className
      )}
    >
      <RadioGroupItem id={optionId} ref={ref} {...props} />
      <span className="flex-1 text-base font-normal text-foreground">{label}</span>
    </label>
  );
});
RadioGroupOption.displayName = 'RadioGroupOption';

export { RadioGroup, RadioGroupItem, RadioGroupOption };
